function parseHex(hex: string): [number, number, number] {
    const h = hex.replace('#', '');
    return [
        parseInt(h.substring(0, 2), 16),
        parseInt(h.substring(2, 4), 16),
        parseInt(h.substring(4, 6), 16),
    ];
}

function luminanceChannel(value: number): number {
    const s = value / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
    return 0.2126 * luminanceChannel(r) + 0.7152 * luminanceChannel(g) + 0.0722 * luminanceChannel(b);
}

function contrastRatio(l1: number, l2: number): number {
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
    const rN = r / 255;
    const gN = g / 255;
    const bN = b / 255;
    const max = Math.max(rN, gN, bN);
    const min = Math.min(rN, gN, bN);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case rN: h = ((gN - bN) / d + (gN < bN ? 6 : 0)) / 6; break;
            case gN: h = ((bN - rN) / d + 2) / 6; break;
            case bN: h = ((rN - gN) / d + 4) / 6; break;
        }
    }
    return [h, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
    if (s === 0) {
        const v = Math.round(l * 255);
        return [v, v, v];
    }
    const hue2rgb = (p: number, q: number, rawT: number): number => {
        const t = rawT < 0 ? rawT + 1 : rawT > 1 ? rawT - 1 : rawT;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    return [
        Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
        Math.round(hue2rgb(p, q, h) * 255),
        Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
    ];
}

function toHex([r, g, b]: [number, number, number]): string {
    return `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Darkens a hex color until it meets the WCAG AA contrast ratio (4.5:1)
 * against the given background. Returns the original color if it already passes.
 */
export function ensureContrast(
    foreground: string,
    background: string,
    minRatio = 4.5,
): string {
    const fgRgb = parseHex(foreground);
    const bgLum = relativeLuminance(parseHex(background));

    if (contrastRatio(relativeLuminance(fgRgb), bgLum) >= minRatio) {
        return foreground;
    }

    const [h, s, l] = rgbToHsl(...fgRgb);
    for (let newL = l; newL >= 0; newL -= 0.01) {
        const candidate = hslToRgb(h, s, newL);
        if (contrastRatio(relativeLuminance(candidate), bgLum) >= minRatio) {
            return toHex(candidate);
        }
    }

    return '#000000';
}
